import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/core/lib/utils';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import type { MyOverdueTaskDto } from '../types';

interface MyOverdueTaskCardProps {
  task: MyOverdueTaskDto;
}

const PRIORITY_STYLES: Record<string, string> = {
  None: 'bg-muted text-muted-foreground',
  Low: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400',
  Medium: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-400',
  High: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-400',
  Urgent: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400',
};

export function MyOverdueTaskCard({ task }: MyOverdueTaskCardProps) {
  const { t } = useTranslation('personalSummary');
  const formatInstant = useInstantFormat();

  const priorityStyle = PRIORITY_STYLES[task.priority ?? 'None'] ?? PRIORITY_STYLES.None;
  const priorityLabel = t(`priority.${task.priority ?? 'None'}`);

  return (
    <Card data-testid={`personal-summary-overdue-card-${task.id}`}>
      <CardContent className="space-y-2 px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <span className="line-clamp-2 text-sm font-medium">{task.title ?? '—'}</span>
          <Badge className={cn('shrink-0', priorityStyle)}>{priorityLabel}</Badge>
        </div>

        <div className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: task.projectColor ?? '#6b7280' }}
          />
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {task.projectName ?? '—'}
          </span>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-destructive">{formatInstant(task.dueDate)}</span>
          <Badge className="bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400">
            +{task.delayDays}d
          </Badge>
        </div>

        <p className="text-xs text-muted-foreground">
          {task.blockedReason ?? t('overdue.motivoEmpty')}
        </p>
      </CardContent>
    </Card>
  );
}
